<template>
  <div>
    <div v-if="$slots.trigger || trigger" @mouseenter="handleToggle" @mouseleave="handleToggle">
      <!-- @slot Optional trigger slot -->
      <slot name="trigger">{{ trigger }}</slot>
    </div>

    <div v-show="isVisible" ref="tooltip" class="gp-tooltip">
      <!-- @slot Content slot -->
      <slot>{{ content }}</slot>
    </div>
  </div>
</template>

<script>
import { computePosition, shift, flip, offset } from '@floating-ui/dom'

/**
 * Displays a tooltip that follows cursor.
 *
 * This component can be used in two ways:
 * - with an external trigger that toggles the `visible` prop
 * - with the `trigger` slot, which will display the tooltip on hover.
 *
 * @preset default { "slots": { "trigger": "<button type=\"button\">Hover me</button>", "default": "<strong>HTML</strong> content" } }
 * @preset props { "props": { "trigger": "hover me", "content": "Simple string" } }
 */
export default {
  name: 'GpTooltip',
  props: {
    /** Whether to display the tooltip. Don't use with the `trigger` slot or prop. */
    visible: { required: false, type: Boolean, default: false },
    /** Optional trigger text. Will be overridden by the `trigger` slot. */
    trigger: { required: false, type: String, default: null },
    /** Optional tooltip content. Will be overridden by the `default` slot. */
    content: { required: false, type: String, default: null },
  },
  data () {
    return {
      isVisible: this.visible,
    }
  },
  methods: {
    handleToggle () {
      this.toggle()
    },
    toggle (value = null) {
      this.isVisible = value !== null ? value : !this.isVisible

      if (this.isVisible) this.listen()
      else this.dontListen()
    },
    listen () {
      document.addEventListener('mousemove', this.listener)
    },
    dontListen () {
      document.removeEventListener('mousemove', this.listener)
    },
    listener ({ clientX, clientY }) {
      const tooltip = this.$refs.tooltip

      const virtualEl = {
        getBoundingClientRect () {
          return {
            width: 0,
            height: 0,
            x: clientX,
            y: clientY,
            left: clientX,
            right: clientX,
            top: clientY,
            bottom: clientY,
          }
        },
      }

      computePosition(virtualEl, tooltip, {
        placement: 'right-start',
        middleware: [offset(5), flip(), shift()],
      }).then(({ x, y }) => {
        Object.assign(tooltip.style, {
          top: `${y}px`,
          left: `${x}px`,
        })
      })
    },
  },
  beforeUnmount () {
    this.toggle(false)
  },
  watch: {
    /**
     * Handles prop change
     *
     * @param {boolean} newValue - New prop value
     */
    visible (newValue) {
      this.toggle(newValue)
    },
  },
}
</script>
